import { useMemo } from 'react'
import { MinimalDropdown, type MinimalOption } from '@ovasabi/ui-minimal'
import { cityLabel, useCities, type City } from '../../lib/places'

export interface CityPickerProps {
  /** Selected city ref ("gn:2352778"), or "" for none. */
  value: string
  onChange: (city: City) => void
  label?: string
  hint?: string
  disabled?: boolean
  /** Lists these cities first, e.g. the customer's area, before A–Z by state. */
  preferredState?: string
}

// The launch market leads the list; every other state follows A–Z.
const LEADING_STATE = 'FCT'

/**
 * One searchable picker over the whole city catalogue (~800 cities, every LGA
 * seat). Search matches the city, its LGA and its state, so "Municipal",
 * "Lagos" and "Kubwa" all find what a person means.
 */
export const CityPicker = ({
  value,
  onChange,
  label = 'City',
  hint,
  disabled,
  preferredState = LEADING_STATE,
}: CityPickerProps) => {
  const { cities, byRef, loading, error } = useCities()

  const options = useMemo<readonly MinimalOption<string>[]>(() => {
    const rank = (c: City) => (c.state === preferredState ? 0 : 1)
    const capital = (c: City) => (c.feature === 'PPLC' || c.feature === 'PPLA' ? 0 : 1)
    return [...cities]
      .sort((a, b) => rank(a) - rank(b) || a.state.localeCompare(b.state) || capital(a) - capital(b) || a.name.localeCompare(b.name))
      .map((city) => ({
        value: city.ref,
        label: city.name,
        description: [city.lga && city.lga !== city.name ? city.lga : '', city.state].filter(Boolean).join(' · '),
        searchableText: `${city.name} ${city.state} ${city.lga ?? ''}`,
      }))
  }, [cities, preferredState])

  return (
    <MinimalDropdown
      label={label}
      value={byRef.has(value) ? value : ''}
      onChange={(ref) => {
        const city = byRef.get(ref)
        if (city) onChange(city)
      }}
      options={options}
      searchable
      searchPlaceholder="Search city, LGA or state"
      placeholder={loading ? 'Loading cities…' : 'Choose a city'}
      hint={error ? 'Cities could not be loaded. Check your connection and try again.' : hint}
      disabled={disabled || loading || error}
      maxVisibleOptions={80}
      renderValue={(option) => {
        const city = option ? byRef.get(option.value) : undefined
        return city ? cityLabel(city) : option?.label
      }}
    />
  )
}
